import { useEffect, useRef, useState } from "react";
import { api, API } from "../lib/api";
import { toast } from "sonner";

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    const r = await api.get("/gallery");
    setItems(r.data);
  };

  useEffect(() => { load(); }, []);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Máx 5MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", caption);
    setUploading(true);
    try {
      await api.post("/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Imagen subida");
      setCaption("");
      if (inputRef.current) inputRef.current.value = "";
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error al subir");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta imagen?")) return;
    try {
      await api.delete(`/gallery/${id}`);
      toast.success("Imagen eliminada");
      load();
    } catch { toast.error("Error"); }
  };

  return (
    <div data-testid="admin-gallery">
      <div className="border border-[color:var(--border-subtle)] rounded-2xl p-6 md:p-8 bg-white/40 mb-8">
        <div className="text-xs uppercase tracking-[0.28em] text-[color:var(--text-secondary)] mb-4">Nueva imagen</div>
        <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-end">
          <div className="flex-1">
            <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--text-secondary)] mb-2 block">Descripción (opcional)</label>
            <input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Sonrisa · Cliente P.M."
              data-testid="gallery-caption"
              className="w-full h-11 px-4 rounded-xl bg-white/60 border border-[color:var(--border-subtle)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]/40"
            />
          </div>
          <label className={`inline-flex items-center gap-2 rounded-full bg-[color:var(--text-primary)] text-[color:var(--bg-primary)] px-6 py-3 text-sm cursor-pointer hover:bg-[color:var(--signature-dark)] transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
            {uploading ? "Subiendo…" : "Subir imagen"}
            <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onFileChange} className="hidden" data-testid="gallery-file-input" />
          </label>
        </div>
        <p className="text-xs text-[color:var(--text-secondary)] mt-3">JPG, PNG o WEBP · máx 5MB. Se muestran en la sección pública "El Trabajo".</p>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-[color:var(--border-subtle)] rounded-2xl p-16 text-center text-[color:var(--text-secondary)]" data-testid="empty-gallery">
          Sin imágenes propias todavía. Las que subas aquí reemplazarán la galería pública.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {items.map((it) => (
            <figure key={it.id} className="relative aspect-square rounded-xl overflow-hidden bg-[color:var(--bg-secondary)] group" data-testid={`gallery-admin-${it.id}`}>
              <img src={`${API}/gallery/${it.id}/image`} alt={it.caption} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <figcaption className="absolute inset-x-3 bottom-3 flex items-center justify-between text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="truncate max-w-[70%]">{it.caption || "Sin título"}</span>
                <button onClick={() => remove(it.id)} data-testid={`gallery-delete-${it.id}`} className="px-2 py-1 rounded-full bg-white/90 text-black text-[10px] uppercase tracking-widest">Eliminar</button>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
    </div>
  );
}
